import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack
} from '@mui/material';
import {
    WhatsApp as WhatsAppIcon,
    Phone,
    CheckCircle,
    ArrowBack,
    Person,
    CardGiftcard,
    AutoAwesome as SparklesIcon
} from '@mui/icons-material';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { masterDataConfigAPI } from '../../../services/api';
import FlutterServicesAPI from '../../../services/flutterServices';
import { isIndiaCountry, COUNTRIES } from '../../../config/businessConstants';

// Debounce helper to avoid too many API calls
let checkUserTimeout = null;

const WhatsAppSignUp = () => {
    const [step, setStep] = useState(1); // 1 = Phone entry, 2 = OTP verification
    const [formData, setFormData] = useState({
        name: '',
        phone_number: '',
        country_code: '+91',
        country_id: COUNTRIES.INDIA.ID, // Set India as default ID
        otp: '',
        referral_code: ''
    });
    const [countries, setCountries] = useState([{ id: COUNTRIES.INDIA.ID, country_code: '+91', name: 'India' }]); // Hardcode India as initial value
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [otpSent, setOtpSent] = useState(false);

    const { setAuthSession } = useMerchantAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Phone validation state
    const [phoneError, setPhoneError] = useState('');
    const [isCheckingUser, setIsCheckingUser] = useState(false);
    const [nameAutoPopulated, setNameAutoPopulated] = useState(false); // Track if name was auto-populated
    const [resendCountdown, setResendCountdown] = useState(0); // Countdown timer for resend OTP in seconds

    // Check if running in Flutter app
    const isFlutterApp = FlutterServicesAPI.isFlutterApp();

    // Mobile number validation (same as MerchantRegister)
    const validateMobile = (phone, countryId) => {
        if (!phone) return { valid: true, error: '' };

        if (isIndiaCountry(countryId)) {
            // India: 10 digits starting with 6-9
            if (!/^[6-9]\d{9}$/.test(phone)) {
                return { valid: false, error: 'Please enter a valid 10-digit Indian mobile number starting with 6-9' };
            }
        } else {
            // Other countries: 5-15 digits
            if (!/^\d{5,15}$/.test(phone)) {
                return { valid: false, error: 'Please enter a valid mobile number (5-15 digits)' };
            }
        }
        return { valid: true, error: '' };
    };

    // Handle phone number change with auto-populate
    const handlePhoneChange = async (value) => {
        const digits = value.replace(/\D/g, '');
        setFormData(prev => ({ ...prev, phone_number: digits }));

        // Clear phone error when user starts typing again
        if (phoneError) setPhoneError('');

        // Auto-populate name when 10 digits are entered (complete number)
        if (digits.length === 10) {
            // Clear any existing timeout
            if (checkUserTimeout) clearTimeout(checkUserTimeout);

            // Debounce the API call by 300ms
            checkUserTimeout = setTimeout(async () => {
                try {
                    setIsCheckingUser(true);
                    const result = await merchantAuthAPI.checkWhatsAppUser({
                        phone_number: digits,
                        country_code: formData.country_code
                    });

                    if (result.data.exists && result.data.name) {
                        // Override name with registered user's name and disable the field
                        setFormData(prev => ({
                            ...prev,
                            name: result.data.name
                        }));
                        setNameAutoPopulated(true); // Disable the name field
                        console.log('🔍 User found, auto-populated name:', result.data.name);
                    } else {
                        // If user not found, allow editing name
                        setNameAutoPopulated(false);
                    }
                } catch (err) {
                    // Silently ignore lookup errors
                    console.log('User lookup failed:', err.message);
                } finally {
                    setIsCheckingUser(false);
                }
            }, 300);
        }
    };

    // Auto-populate referral code from URL
    useEffect(() => {
        const referralCode = searchParams.get('ref') || searchParams.get('referral_code');
        if (referralCode) {
            setFormData(prev => ({
                ...prev,
                referral_code: referralCode
            }));
            console.log('🎯 Referral code auto-populated from URL:', referralCode);
        }
    }, [searchParams]);

    // Mobile background logic
    useEffect(() => {
        const isMobile = window.innerWidth < 600; // xs breakpoint
        if (isMobile) {
            document.body.style.backgroundColor = 'white';
        } else {
            document.body.style.backgroundColor = '#f5f5f5';
        }
        return () => {
            document.body.style.backgroundColor = '#f5f5f5';
        };
    }, []);

    // Countdown timer effect for resend OTP
    useEffect(() => {
        if (resendCountdown > 0) {
            const timer = setTimeout(() => {
                setResendCountdown(resendCountdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCountdown]);

    // Load countries on mount
    useEffect(() => {
        const loadCountries = async () => {
            try {
                const response = await masterDataConfigAPI.getCountries();
                const countriesData = response.data.data || [];

                if (countriesData.length > 0) {
                    setCountries(countriesData);

                    // Find India in the fresh data to ensure we have the correct ID/code from server
                    const indiaCountry = countriesData.find(c => isIndiaCountry(c.id));
                    if (indiaCountry) {
                        setFormData(prev => ({
                            ...prev,
                            country_id: indiaCountry.id,
                            country_code: indiaCountry.country_code
                        }));
                    }
                }
            } catch (err) {
                console.error('Failed to load countries:', err);
            }
        };
        loadCountries();
    }, []);

    const handleCountryChange = (e) => {
        const selectedCountryId = e.target.value;
        const selectedCountry = countries.find(country => country.id === selectedCountryId);

        setFormData({
            ...formData,
            country_id: selectedCountryId,
            country_code: selectedCountry ? selectedCountry.country_code : ''
        });
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate country code
        if (!formData.country_id) {
            setError('Please select a country code');
            return;
        }

        // Validate phone number - show error below the input field
        const validation = validateMobile(formData.phone_number, formData.country_id);
        if (!validation.valid) {
            setPhoneError(validation.error);
            return;
        }
        if (!formData.phone_number || formData.phone_number.length < 10) {
            setPhoneError('Please enter a valid phone number');
            return;
        }

        setLoading(true);

        try {
            const result = await merchantAuthAPI.sendWhatsAppOTP({
                phone_number: formData.phone_number,
                country_code: formData.country_code
            });

            if (result.data.success) {
                setSuccess('OTP sent to your WhatsApp number!');
                setOtpSent(true);
                setStep(2);
                setResendCountdown(60); // Start 1-minute countdown
            } else {
                setError(result.data.error || 'Failed to send OTP');
            }
        } catch (err) {
            console.error('Send OTP error:', err);
            setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.otp || formData.otp.length !== 4) {
            setError('Please enter the 4-digit OTP');
            return;
        }

        setLoading(true);

        try {
            console.log('🔍 Verifying WhatsApp OTP...', {
                phone_number: formData.phone_number,
                country_code: formData.country_code,
                otp: formData.otp,
                name: formData.name,
                referral_code: formData.referral_code
            });

            const requestPayload = {
                phone_number: formData.phone_number,
                country_code: formData.country_code,
                otp: formData.otp,
                name: formData.name.trim(),
                referral_code: formData.referral_code.trim() || undefined
            };

            console.log('📤 Sending OTP verification request with payload:', requestPayload);
            console.log('🎁 Referral code value:', formData.referral_code);
            console.log('🎁 Referral code (trimmed):', formData.referral_code.trim());
            console.log('🎁 Referral code is empty?', !formData.referral_code.trim());

            const result = await merchantAuthAPI.verifyWhatsAppOTP(requestPayload);

            console.log('📥 OTP Verification Response:', result.data);

            if (result.data.success) {
                console.log('✅ OTP Verified Successfully!');
                console.log('📦 Access Token:', result.data.access_token);
                console.log('👤 Merchant Data:', result.data.merchant);
                console.log('🆕 Is New User:', result.data.is_new_user);
                console.log('✉️ Email Verified:', result.data.merchant?.email_verified);

                // Save token and user data using context
                setAuthSession(result.data.merchant, result.data.access_token);

                // Trigger Flutter services only if in Flutter app (mobile)
                // This is required to save FCM token after WhatsApp login
                if (isFlutterApp) {
                    try {
                        console.log('📱 Mobile app detected, registering device info after WhatsApp login...');
                        const deviceInfo = await FlutterServicesAPI.getDeviceInfo();
                        console.log('📱 Device info received after WhatsApp login:', deviceInfo);
                    } catch (deviceError) {
                        console.warn('⚠️ Failed to register device info after WhatsApp login:', deviceError);
                    }
                }

                // setSuccess(result.data.is_new_user ? 'Registration successful!' : 'Login successful!');

                // Redirect to dashboard using navigate for smooth transition
                console.log('🚀 Redirecting to dashboard in 1 second...');
                setTimeout(() => {
                    console.log('➡️ Navigating to /dashboard via SPA routing');
                    navigate('/dashboard');
                }, 1000);
            } else {
                console.error('❌ OTP Verification Failed:', result.data.error);
                setError(result.data.error || 'Invalid OTP');
            }
        } catch (err) {
            console.error('💥 Verify OTP error:', err);
            console.error('💥 Error response:', err.response?.data);
            setError(err.response?.data?.error || 'Invalid or expired OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const result = await merchantAuthAPI.sendWhatsAppOTP({
                phone_number: formData.phone_number,
                country_code: formData.country_code
            });

            if (result.data.success) {
                setSuccess('New OTP sent to your WhatsApp!');
                setResendCountdown(60); // Restart 1-minute countdown
            } else {
                setError('Failed to resend OTP');
            }
        } catch (err) {
            setError('Failed to resend OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="sm" sx={{ minHeight: '100vh' }} data-testid="whatsapp-signup-page">
            <Box
                sx={{
                    marginTop: { xs: 0.5, sm: 8 }, // Further reduced top margin for mobile
                    marginBottom: { xs: 1, sm: 0 }, // Reduced bottom margin for mobile
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: 2, sm: 5, md: 5 }, // Further reduced padding for mobile
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderRadius: '18px', // Changed from 2 to 18px
                        width: '100%',
                        boxShadow: { xs: 'none', sm: 3 }, // No shadow for mobile, shadow for larger screens
                        border: { xs: 'none', sm: '1px solid #E0E0E0' }, // No border on mobile, border on larger screens
                        '@media (min-width: 414px)': {
                            padding: 4, // Larger padding for 414px+ devices
                        }
                    }}
                >

                    {/* Back Button */}
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate('/login')}
                            sx={{
                                color: '#6318C6',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: 'rgba(99, 24, 198, 0.04)'
                                }
                            }}
                        >
                            Back to Login
                        </Button>
                    </Box>

                    {/* Glammify Logo */}
                    <Box
                        component="img"
                        src="/assets/img/glammify-logo.png"
                        alt="Glammify Logo"
                        sx={{
                            maxWidth: { xs: '200px', sm: '300px', md: '300px' },
                            height: 'auto',
                            width: '100%',
                            marginBottom: { xs: '20px', sm: '40px' },
                            marginTop: '15px'
                        }}
                    />

                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                            {step === 1 ? 'Sign In with WhatsApp' : 'Verify OTP'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {step === 1
                                ? 'Get started instantly with your WhatsApp number'
                                : `Verification code sent to ${formData.country_code} ${formData.phone_number}`}
                        </Typography>
                    </Box>

                    {/* Alert messages */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}

                    {/* Step 1: Phone Number Entry */}
                    {step === 1 && (
                        <form onSubmit={handleSendOTP} style={{ width: '100%' }}>
                            <Stack spacing={2.5}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {/* Country Code Selector */}
                                    <FormControl required sx={{ minWidth: { xs: 100, sm: 120 } }}>
                                        <InputLabel id="country-select-label">Code</InputLabel>
                                        <Select
                                            labelId="country-select-label"
                                            value={formData.country_id || ''}
                                            label="Code *"
                                            onChange={handleCountryChange}
                                            required
                                            disabled={loading}
                                            sx={{
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#6318C6',
                                                },
                                            }}
                                        >
                                            {countries.map((country) => (
                                                <MenuItem key={country.id} value={country.id}>
                                                    {country.country_code}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Phone Number Input */}
                                    <TextField
                                        required
                                        fullWidth
                                        label="WhatsApp Number"
                                        value={formData.phone_number}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        disabled={loading || isCheckingUser}
                                        placeholder="Mobile Number"
                                        type="tel"
                                        inputMode="tel"
                                        error={!!phoneError}
                                        helperText={phoneError}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Phone sx={{ color: phoneError ? '#d32f2f' : '#6318C6' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: phoneError ? '#d32f2f' : '#6318C6',
                                                },
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: phoneError ? '#d32f2f' : '#6318C6',
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Name Input */}
                                <TextField
                                    required={!nameAutoPopulated}
                                    fullWidth
                                    label="Name"
                                    value={formData.name}
                                    onChange={(e) => !nameAutoPopulated && setFormData({ ...formData, name: e.target.value })}
                                    disabled={loading || nameAutoPopulated}
                                    placeholder="Enter your name"
                                    helperText={nameAutoPopulated ? "Name auto-filled from your registered account" : ""}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person sx={{ color: nameAutoPopulated ? '#9e9e9e' : '#6318C6' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&.Mui-focused fieldset': {
                                                borderColor: nameAutoPopulated ? '#9e9e9e' : '#6318C6',
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: nameAutoPopulated ? 'rgba(0, 0, 0, 0.06)' : 'inherit',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: nameAutoPopulated ? '#9e9e9e' : '#6318C6',
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: '#757575',
                                        },
                                    }}
                                />

                                {/* Send OTP Button */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={loading || !formData.name.trim() || formData.phone_number.length < 10 || !formData.country_id}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
                                    sx={{
                                        mt: 2,
                                        py: 1.5,
                                        bgcolor: '#25D366',
                                        '&:hover': {
                                            bgcolor: '#20BD5A',
                                        },
                                        '&:disabled': {
                                            bgcolor: '#e0e0e0',
                                        },
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '8px'
                                    }}
                                >
                                    {loading ? 'Sending OTP...' : 'Send OTP via WhatsApp'}
                                </Button>

                                {/* Continue as Guest Button - Required for App Store Compliance */}
                                {isFlutterApp && (
                                    <Button
                                        fullWidth
                                        variant="text"
                                        sx={{
                                            mt: 1,
                                            py: 1,
                                            color: '#6318C6',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(99, 24, 198, 0.04)',
                                                textDecoration: 'underline'
                                            }
                                        }}
                                        onClick={() => navigate('/guest-gallery')}
                                        startIcon={<SparklesIcon />}
                                    >
                                        Continue as Guest
                                    </Button>
                                )}
                            </Stack>
                        </form>
                    )}

                    {/* Step 2: OTP Verification */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyOTP} style={{ width: '100%' }}>
                            <Stack spacing={3}>
                                {/* OTP Input */}
                                <TextField
                                    required
                                    fullWidth
                                    label="Enter 4-digit Code"
                                    value={formData.otp}
                                    onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '') })}
                                    disabled={loading}
                                    placeholder="0000"
                                    type="tel"
                                    inputMode="tel"
                                    inputProps={{
                                        maxLength: 4,
                                        style: { textAlign: 'center', fontSize: '2rem', letterSpacing: '0.8rem', fontWeight: 700 }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#6318C6',
                                            },
                                            borderRadius: '12px'
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#6318C6',
                                        },
                                    }}
                                />

                                {/* Verify Button */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={loading || formData.otp.length !== 4}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                                    sx={{
                                        py: 1.5,
                                        bgcolor: '#6318C6',
                                        '&:hover': {
                                            bgcolor: '#5014A8',
                                        },
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '8px'
                                    }}
                                >
                                    {loading ? 'Verifying...' : 'Verify & Continue'}
                                </Button>

                                {/* Resend OTP */}
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Didn't receive the code?{' '}
                                        <Button
                                            onClick={handleResendOTP}
                                            disabled={loading || resendCountdown > 0}
                                            sx={{
                                                textTransform: 'none',
                                                color: resendCountdown > 0 ? '#9e9e9e' : '#6318C6',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    bgcolor: 'transparent',
                                                    textDecoration: resendCountdown > 0 ? 'none' : 'underline'
                                                },
                                                '&:disabled': {
                                                    color: '#9e9e9e'
                                                }
                                            }}
                                        >
                                            {resendCountdown > 0
                                                ? `Resend OTP (${resendCountdown}s)`
                                                : 'Resend OTP'
                                            }
                                        </Button>
                                    </Typography>
                                </Box>
                                {/* Back to Phone Entry (Manual Addition for UX) */}
                                <Button
                                    onClick={() => setStep(1)}
                                    color="inherit"
                                    sx={{ textTransform: 'none', mt: 1 }}
                                >
                                    Change Phone Number
                                </Button>
                            </Stack>
                        </form>
                    )}
                </Paper>

                {/* Footer */}
                <Box sx={{ mt: { xs: 1, sm: 4 }, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Transform your business with AI-generated visuals
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        © {new Date().getFullYear()} Glammify. All rights reserved.
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
};

export default WhatsAppSignUp;
